import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom gold marker
const goldIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-gold.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Coordinates for cities
const CITY_COORDS = {
  Limoges: [45.8336, 1.2611],
  "Évaux-les-Bains": [46.1736, 2.4847],
  "Evaux-les-Bains": [46.1736, 2.4847],
};

export default function LogementsMap({ logements = [], onSelectLogement }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    // Dark theme tiles
    const map = L.map(mapRef.current, {
      center: [46.0, 1.85],
      zoom: 9,
      zoomControl: false,
      scrollWheelZoom: false,
    });

    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      maxZoom: 19,
    }).addTo(map);

    L.control.zoom({ position: "bottomright" }).addTo(map);

    mapInstance.current = map;

    return () => {
      map.remove();
      mapInstance.current = null;
    };
  }, []);

  // Update markers when logements change
  useEffect(() => {
    if (!mapInstance.current) return;

    // Clear old markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    if (logements.length === 0) return;

    const bounds = [];

    logements.forEach((logement) => {
      const cityCoords = CITY_COORDS[logement.ville];
      if (!cityCoords) return;

      // Slight random offset to avoid stacking
      const lat = cityCoords[0] + (Math.random() - 0.5) * 0.01;
      const lng = cityCoords[1] + (Math.random() - 0.5) * 0.01;

      const marker = L.marker([lat, lng], { icon: goldIcon }).addTo(mapInstance.current);

      const popupContent = `
        <div style="font-family: system-ui; min-width: 180px;">
          <strong style="color: #004333; font-size: 14px;">${logement.titre}</strong><br/>
          <span style="color: #6B5E50; font-size: 12px;">${logement.ville} &bull; ${logement.capacite} pers.</span><br/>
          <span style="color: #C9922A; font-weight: bold; font-size: 14px; margin-top: 4px; display: inline-block;">${logement.prix_nuit} EUR/nuit</span>
        </div>
      `;

      marker.bindPopup(popupContent, {
        className: "custom-popup",
        closeButton: false,
      });

      marker.on("click", () => {
        if (onSelectLogement) onSelectLogement(logement);
      });

      markersRef.current.push(marker);
      bounds.push([lat, lng]);
    });

    if (bounds.length > 0) {
      mapInstance.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [logements, onSelectLogement]);

  return (
    <div
      ref={mapRef}
      className="w-full h-full rounded-2xl overflow-hidden"
      style={{ minHeight: "400px" }}
      data-testid="logements-map"
    />
  );
}
